/**
 * The one description of a Song Slide's geometry.
 *
 * A slide is drawn twice — as React in `SongSlidePageView` and as shapes in
 * `pptxExport` — and the two had drifted: the deck pinned its rows at 33.8% /
 * 17.8% / 33.8% / 14.7% of a cell while the screen let the icon rows flex to
 * whatever was left (≈36%), and a rehearsal banner pushed the screen's grid down
 * while the deck drew the banner *underneath* the top row's icons. So a slide
 * previewed as one layout and exported as another.
 *
 * Everything here is therefore a *fraction* or an inch measurement of the slide
 * itself, never a pixel: the same numbers have to survive being a 104px rail
 * thumbnail, a projector screen, and a 10" PowerPoint page.
 */

import type { SlideRowKind, SlideRowSpec } from "./slideRows";

/** pptxgenjs `LAYOUT_WIDE`, which is what the deck is built on. */
export const SLIDE_WIDTH_IN = 10;
export const SLIDE_HEIGHT_IN = 5.625;

/** One quadrant of the 2×2 grid. Cells are always equal — see `SongSlidePageView`. */
export const SLIDE_CELL_HEIGHT_IN = SLIDE_HEIGHT_IN / 2;

/**
 * The band a section label sits in, at the top of a *measure*.
 *
 * A label used to be one banner across the whole slide, which meant a slide
 * showed the first of its four measures' labels and silently dropped the rest —
 * a section could only ever begin every fourth bar. Each measure now draws its
 * own pill, in the band this reserves.
 *
 * 0.32" rather than the banner's 0.35": a pill is shorter than a full-width
 * band, and this height is now paid once per *row* of the grid rather than once
 * per slide. It is still the smaller number the 16pt pill fits inside — see
 * `.song-slide-section-label`, whose type, padding and rule add up to 3.04cqw
 * against this band's 3.2cqw.
 */
export const SECTION_LABEL_HEIGHT_IN = 0.32;
export const SECTION_LABEL_FONT_PT = 16;

/** The label band as a fraction of the cell it is reserved inside. */
export const SECTION_BAND_FRACTION = SECTION_LABEL_HEIGHT_IN / SLIDE_CELL_HEIGHT_IN;

/**
 * Rounded to four places before it reaches CSS.
 *
 * `0.35 / 10 * 100` is `3.4999999999999996` in binary floating point, and that
 * is what would land in the style attribute otherwise. Four places is far finer
 * than a projector can resolve.
 */
function toCqw(fractionOfWidth: number): number {
  return Math.round(fractionOfWidth * 100 * 10_000) / 10_000;
}

/**
 * Inches → `cqw`, the unit the React side sizes in.
 *
 * The slide is its own container-query container, so 1cqw is 1% of the slide's
 * width — and the deck's slide is 10" wide, making 1cqw exactly 0.1".
 */
export function inchesToCqw(inches: number): number {
  return toCqw(inches / SLIDE_WIDTH_IN);
}

/** Points → `cqw`, for type sized to match the deck's own font sizes. */
export function pointsToCqw(points: number): number {
  return toCqw(points / (SLIDE_WIDTH_IN * 72));
}

/**
 * Row heights inside a cell, as fractions of the cell's height.
 *
 * The two fixed rows come from the deck's original inch measurements — 0.5" and
 * 0.4125" of a 2.8125" cell — kept as ratios so they still hold once a band has
 * taken height off the cell.
 */
export const NUMBER_TRAIN_ROW_FRACTION = 0.5 / 2.8125;
export const LYRICS_ROW_FRACTION = 0.4125 / 2.8125;

export interface SlideRowSpan {
  partIndex: number;
  kind: SlideRowKind;
  /** Which artwork an iconic row draws. Absent on the other kinds. */
  level?: 1 | 2;
  /** Top edge, as a fraction of the cell's height. */
  top: number;
  /** Height, as a fraction of the cell's height. */
  height: number;
}

export interface SlideRowLayoutOptions {
  /**
   * Whether the cell reserves the section-label band at its top.
   *
   * Decided per *slide*, not per measure: every row inside a cell places its
   * icons, digits and syllables as percentages of that cell, so a cell shorter
   * than the one beside it renders the same measure at a different scale. One
   * labeled measure therefore costs all four cells the band, and a label can be
   * added or moved without the rest of the slide shifting under it.
   */
  hasLabelBand?: boolean;
}

function fixedFractionFor(kind: SlideRowKind): number {
  if (kind === "numberTrain") return NUMBER_TRAIN_ROW_FRACTION;
  if (kind === "lyrics") return LYRICS_ROW_FRACTION;
  return 0;
}

/**
 * Stack the parts into a cell, top to bottom, in the order they are given.
 *
 * Ordered by the parts array rather than by name: the screen has always drawn
 * them in that order, while the deck placed each row by looking its name up —
 * which silently drew a fifth part on top of the Bass row. One rule now.
 *
 * The stack always fills whatever the label band leaves. Icon rows share what
 * the fixed rows leave — with the four-part preset and no band that comes to
 * exactly the deck's original inch measurements — and when there are *no* icon
 * rows to take up the slack, the fixed rows themselves are scaled to fit. A
 * two-part Number-Train-and-lyric slide claimed 32% of its cell before that,
 * projecting a bar and a word into the top third of the screen with two thirds
 * of white under them.
 */
export function computeSlideRowLayout(
  specs: readonly SlideRowSpec[],
  { hasLabelBand = false }: SlideRowLayoutOptions = {}
): SlideRowSpan[] {
  // What is left of the cell once a labeled slide has taken its band. The
  // fixed rows keep their absolute heights inside it and the icon rows absorb
  // the difference, which is what stops a label squashing a lyric out of
  // legibility on the one slide that carries a section name.
  const available = hasLabelBand ? Math.max(1 - SECTION_BAND_FRACTION, 0) : 1;
  const fixedTotal = specs.reduce((sum, spec) => sum + fixedFractionFor(spec.kind), 0);
  const iconicCount = specs.filter((spec) => spec.kind === "iconic").length;

  // Two cases scale the fixed rows. With no icon row to absorb the remainder
  // they are the whole stack, so they stretch to fill it; and enough of them to
  // claim more than the cell are squeezed back down, which is what stops the last
  // rows dropping out the bottom of a cell that clips.
  const needsScale = iconicCount === 0 || fixedTotal > available;
  const scaleFixed = needsScale && fixedTotal > 0 ? available / fixedTotal : 1;
  const iconicHeight = iconicCount > 0 ? Math.max(available - fixedTotal, 0) / iconicCount : 0;

  let top = hasLabelBand ? SECTION_BAND_FRACTION : 0;
  return specs.map((spec, partIndex) => {
    const height = spec.kind === "iconic" ? iconicHeight : fixedFractionFor(spec.kind) * scaleFixed;
    const span: SlideRowSpan = { partIndex, kind: spec.kind, top, height };
    if (spec.level !== undefined) span.level = spec.level;
    top += height;
    return span;
  });
}
